import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Loader2, Mail, Lock, User, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";

const EMERGENT_LOGIN = "https://auth.emergentagent.com/";

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = new URLSearchParams(location.search).get("next") || "/profile";

  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      await signup(form.email, form.password, form.name);
      toast.success("Account created — welcome!");
      navigate(redirectTo);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create account");
    } finally {
      setBusy(false);
    }
  };

  const googleSignIn = () => {
    const callback = `${window.location.origin}/auth/callback?next=${encodeURIComponent(redirectTo)}`;
    window.location.href = `${EMERGENT_LOGIN}?redirect=${encodeURIComponent(callback)}`;
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Header />
      <main className="flex-1 flex items-center justify-center px-5 py-24 pt-32">
        <Card className="w-full max-w-md p-7 shadow-xl" data-testid="signup-card">
          <h1 className="font-display text-3xl font-semibold text-brand-navy mb-1.5">Create your account</h1>
          <p className="text-sm text-slate-600 mb-6">One account to schedule pickups and save your card on file.</p>

          <Button
            type="button"
            onClick={googleSignIn}
            variant="outline"
            className="w-full h-11 gap-2.5 font-semibold"
            data-testid="signup-google-button"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Sign up with Google
          </Button>

          <div className="flex items-center gap-3 my-5 text-xs uppercase tracking-wider text-slate-400">
            <div className="flex-1 h-px bg-slate-200" /> or <div className="flex-1 h-px bg-slate-200" />
          </div>

          <form onSubmit={submit} className="space-y-3.5">
            <div>
              <Label htmlFor="signup-name">Full name</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="signup-name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="pl-9"
                  data-testid="signup-name-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="signup-email">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="signup-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="pl-9"
                  data-testid="signup-email-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="signup-password">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="signup-password"
                  type="password"
                  required
                  minLength={8}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="pl-9"
                  data-testid="signup-password-input"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">At least 8 characters.</p>
            </div>
            <Button type="submit" disabled={busy} className="w-full h-11 gap-2 mt-1" data-testid="signup-submit-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
              {busy ? "Creating account…" : "Create account"}
            </Button>
          </form>

          <p className="text-sm text-slate-600 mt-5 text-center">
            Already have an account?{" "}
            <Link to={`/login?next=${encodeURIComponent(redirectTo)}`} className="text-brand-blue font-semibold hover:underline" data-testid="signup-login-link">
              Sign in
            </Link>
          </p>
        </Card>
      </main>
      <Footer />
    </div>
  );
}
